import type { ReactNode } from "react";

export type Tone = "neutral" | "brand" | "success" | "warning" | "danger" | "info";

const TONE: Record<Tone, string> = {
  neutral: "bg-ink/[0.05] text-muted ring-ink/10",
  brand: "bg-brand-light text-brand-ink ring-brand-border",
  success: "bg-success/10 text-success ring-success/25",
  warning: "bg-warning/10 text-warning ring-warning/30",
  danger: "bg-danger/10 text-danger ring-danger/25",
  info: "bg-info/10 text-info ring-info/25",
};

const DOT: Record<Tone, string> = {
  neutral: "bg-muted",
  brand: "bg-brand",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
};

/**
 * A status pill. Semantic tones lead with a dot, so the state still reads to
 * someone who cannot tell the tints apart, and in bright light on a phone.
 */
export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  const dotted = tone !== "neutral" && tone !== "brand";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset ${TONE[tone]}`}
    >
      {dotted ? <span aria-hidden="true" className={`size-1.5 shrink-0 rounded-full ${DOT[tone]}`} /> : null}
      {children}
    </span>
  );
}

/** A filled dot plus label — reads faster than colour alone in a dense table. */
export function StatusDot({ tone, label }: { tone: Tone; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-ink">
      <span aria-hidden="true" className="relative flex size-2">
        {tone === "success" ? (
          <span className={`absolute inset-0 rounded-full opacity-35 ${DOT[tone]} scale-[1.9]`} />
        ) : null}
        <span className={`relative size-2 rounded-full ${DOT[tone]}`} />
      </span>
      {label}
    </span>
  );
}

/** Maps a user or org status to a tone, so every screen shows it identically. */
export function statusTone(status: string): Tone {
  switch (status) {
    case "active":
    case "approved":
      return "success";
    case "pending":
    case "pending_counter":
    case "pending_sales_rep":
    case "pending_org_admin":
      return "warning";
    case "suspended":
    case "rejected":
      return "danger";
    default:
      return "neutral";
  }
}
